import { Alert, Typography } from "@platform/design-system";

interface CensusUnreadColumnsProps {
  readonly headers: readonly string[];
}

/**
 * Columns this census carried that reading did not use.
 *
 * Informational, not a warning: a census always carries columns we will never
 * store, and most of these are fine. It earns its place because it answers
 * the one question a failed check leaves open — whether the value was absent
 * from the file, or present under a heading we do not know.
 *
 * No claim is made about which heading answers which field. Matching them is
 * guesswork we would often be wrong about, and the analyst has the file.
 */
export const CensusUnreadColumns = ({
  headers,
}: CensusUnreadColumnsProps): React.JSX.Element | null => {
  if (headers.length === 0) {
    return null;
  }

  return (
    <Alert severity="info" data-testid="census-unread-columns">
      <Typography variant="subtitle2">
        {headers.length} {headers.length === 1 ? "column" : "columns"} in this
        census {headers.length === 1 ? "was" : "were"} not read
      </Typography>
      <Typography variant="body-medium">{headers.join(" · ")}</Typography>
      <Typography variant="caption" color="textSecondary">
        Some are columns we never store. Where a check below is missing, one of
        these may be the column it was looking for.
      </Typography>
    </Alert>
  );
};
